import { useCallback, useEffect, useState } from "react";
import { api } from "../utils/axios-instance";

/**
 * Mirrors useTickets.js's shape and conventions, but for the Projects/Tasks
 * table view: `projects` is a flat array, each entry carrying its own
 * embedded `tasks` array (the /tickets/projects/ endpoint returns both in
 * one round trip so the whole table renders without a waterfall of
 * per-project task fetches).
 */
export function useProjects({ visibility }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/tickets/projects/", {
        params: { visibility },
      });
      setProjects(res.data || []);
    } catch (err) {
      console.error("Failed to load projects", err);
    } finally {
      setLoading(false);
    }
  }, [visibility]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const createProject = useCallback(async (payload) => {
    const res = await api.post("/tickets/projects/", payload);
    setProjects((prev) => [...prev, { ...res.data, tasks: [] }]);
    return res.data;
  }, []);

  const updateProject = useCallback(async (projectId, changes) => {
    const res = await api.patch(`/tickets/projects/${projectId}/`, changes);
    setProjects((prev) =>
      prev.map((p) => (p.id === projectId ? { ...p, ...res.data } : p))
    );
    return res.data;
  }, []);

  const deleteProject = useCallback(
    async (projectId) => {
      const previous = projects;
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      try {
        await api.delete(`/tickets/projects/${projectId}/`);
      } catch (err) {
        console.error("Delete project failed, reverting", err);
        setProjects(previous);
        throw err;
      }
    },
    [projects]
  );

  const patchTaskInProject = useCallback((projectId, taskId, patch) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id !== projectId
          ? p
          : {
              ...p,
              tasks: (p.tasks || []).map((t) =>
                t.id === taskId ? { ...t, ...patch } : t
              ),
            }
      )
    );
  }, []);

  const addTask = useCallback(async (projectId, payload = {}) => {
    const res = await api.post(
      `/tickets/projects/${projectId}/tasks/`,
      payload
    );
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, tasks: [...(p.tasks || []), res.data] } : p
      )
    );
    return res.data;
  }, []);

  const updateTask = useCallback(
    async (projectId, taskId, changes) => {
      patchTaskInProject(projectId, taskId, changes); // optimistic
      try {
        const res = await api.patch(
          `/tickets/projects/${projectId}/tasks/${taskId}/`,
          changes
        );
        patchTaskInProject(projectId, taskId, res.data);
        return res.data;
      } catch (err) {
        console.error("Update task failed, reverting", err);
        fetchProjects();
        throw err;
      }
    },
    [patchTaskInProject, fetchProjects]
  );

  const moveTask = useCallback(
    async (projectId, taskId, newStatus) => {
      patchTaskInProject(projectId, taskId, { status: newStatus }); // optimistic
      try {
        await api.patch(
          `/tickets/projects/${projectId}/tasks/${taskId}/move/`,
          {
            status: newStatus,
            order: 0,
          }
        );
      } catch (err) {
        console.error("Move task failed, reverting", err);
        fetchProjects();
        throw err;
      }
    },
    [patchTaskInProject, fetchProjects]
  );

  const assignTask = useCallback(
    async (projectId, taskId, assigneeUid) => {
      try {
        const res = await api.patch(
          `/tickets/projects/${projectId}/tasks/${taskId}/assign/`,
          { assignee_uid: assigneeUid }
        );
        patchTaskInProject(projectId, taskId, res.data);
        return res.data;
      } catch (err) {
        console.error("Assign task failed, reverting", err);
        fetchProjects();
        throw err;
      }
    },
    [patchTaskInProject, fetchProjects]
  );

  const deleteTask = useCallback(
    async (projectId, taskId) => {
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, tasks: (p.tasks || []).filter((t) => t.id !== taskId) }
            : p
        )
      );
      try {
        await api.delete(`/tickets/projects/${projectId}/tasks/${taskId}/`);
      } catch (err) {
        console.error("Delete task failed, reverting", err);
        fetchProjects();
        throw err;
      }
    },
    [fetchProjects]
  );

  return {
    projects,
    loading,
    createProject,
    updateProject,
    deleteProject,
    addTask,
    updateTask,
    moveTask,
    assignTask,
    deleteTask,
    refetch: fetchProjects,
  };
}
